import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  PencilIcon,
  TrashIcon,
  FolderIcon,
  PhotoIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";

import { toast } from "react-toastify";

import ApiCustom from "../../utils/ApiCustom";

import MediaModal from "../../components/admin/MediaModal";

import AdvertisementVideoModal from "../../components/admin/AdvertisementVideoModal";

import { fetchAdvertisementMedias } from "../../features/admin/MediaSlice";

const AD_PARENT_SLUG = "quang-cao";

const FIXED_DESKTOP_POSITION_KEY = "desktop_replace_posts_right";

const FIXED_MOBILE_POSITION_KEY = "mobile_before_register";

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");

  return permissions
    ? JSON.parse(permissions).map((p) => p.permissionSlug)
    : [];
};

const userId = localStorage.getItem("userId");

const hasPermission = (requiredPermission) => {
  if (userId == 1) return true;

  const userPermissions = getUserPermissions();

  return userPermissions.includes(requiredPermission);
};

const slugify = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
};

const AdvertisementMedias = () => {
  const dispatch = useDispatch();

  const { advertisementMedias, advertisementStatus, error } = useSelector(
    (state) => state.medias
  );

  const [mediaCategories, setMediaCategories] = useState([]);

  const [adParentCategory, setAdParentCategory] = useState(null);

  const [currentCategory, setCurrentCategory] = useState(null);

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);

  const [editingCategory, setEditingCategory] = useState(null);

  const [categoryName, setCategoryName] = useState("");

  const [categoryType, setCategoryType] = useState("image");

  const [categoryError, setCategoryError] = useState("");

  const [categorySaving, setCategorySaving] = useState(false);

  const [deleteCategory, setDeleteCategory] = useState(null);

  const [selectedMedia, setSelectedMedia] = useState(null);

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const fetchMediaCategories = async () => {
    try {
      const response = await ApiCustom.get("/media-category-shows", {
        params: {
          limit: 1000,
        },
      });

      const data = response.data?.data?.data || [];

      const categories = Array.isArray(data) ? data : [];

      const parent = categories.find(
        (category) => category.slug === AD_PARENT_SLUG
      );

      setMediaCategories(categories);

      setAdParentCategory(parent || null);

      if (parent) {
        const children = categories.filter(
          (category) => Number(category.parentId) === Number(parent.id)
        );

        if (!currentCategory && children.length > 0) {
          setCurrentCategory(children[0]);
        }

        if (
          currentCategory &&
          !children.some(
            (category) => Number(category.id) === Number(currentCategory.id)
          )
        ) {
          setCurrentCategory(children[0] || null);
        }
      }
    } catch (error) {
      toast.error("Không tải được danh mục quảng cáo");
    }
  };

  useEffect(() => {
    dispatch(fetchAdvertisementMedias());

    fetchMediaCategories();
  }, [dispatch]);

  useEffect(() => {
    if (advertisementStatus === "idle") {
      dispatch(fetchAdvertisementMedias());
    }
  }, [advertisementStatus, dispatch]);

  const adChildCategories = adParentCategory
    ? mediaCategories.filter(
        (category) => Number(category.parentId) === Number(adParentCategory.id)
      )
    : [];

  const currentMedias = currentCategory
    ? (advertisementMedias || []).filter(
        (media) => Number(media.mediaCategoryId) === Number(currentCategory.id)
      )
    : [];

  const handleOpenCategoryModal = (category = null) => {
    setEditingCategory(category);

    setCategoryName(category?.name || "");

    setCategoryType(category?.type || "image");

    setCategoryError("");

    setIsCategoryModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    setIsCategoryModalOpen(false);

    setEditingCategory(null);

    setCategoryName("");

    setCategoryType("image");

    setCategoryError("");

    setCategorySaving(false);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();

    const name = categoryName.trim();

    if (!adParentCategory) {
      setCategoryError("Chưa có danh mục cha Quảng cáo");

      return;
    }

    if (!name) {
      setCategoryError("Tên thư mục quảng cáo không được để trống");

      return;
    }

    const payload = {
      name,
      slug: slugify(name),
      parentId: adParentCategory.id,
      type: categoryType,
      positionKey: FIXED_DESKTOP_POSITION_KEY,
      mobilePositionKey: FIXED_MOBILE_POSITION_KEY,
      status: editingCategory?.status ?? true,
    };

    try {
      setCategorySaving(true);

      if (editingCategory) {
        await ApiCustom.put(
          `/media-category-update/${editingCategory.id}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        toast.success("Cập nhật thư mục quảng cáo thành công");
      } else {
        await ApiCustom.post("/media-category-create", payload, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        toast.success("Tạo thư mục quảng cáo thành công");
      }

      handleCloseCategoryModal();

      await fetchMediaCategories();
    } catch (error) {
      toast.error(
        error.response?.data?.messages ||
          error.response?.data?.message ||
          "Lưu thư mục quảng cáo thất bại"
      );
    } finally {
      setCategorySaving(false);
    }
  };

  const handleConfirmDeleteCategory = async () => {
    if (!deleteCategory) return;

    try {
      await ApiCustom.delete(`/media-category-delete/${deleteCategory.id}`);

      if (Number(currentCategory?.id) === Number(deleteCategory.id)) {
        setCurrentCategory(null);
      }

      toast.success("Xóa thư mục quảng cáo thành công");

      setDeleteCategory(null);

      await fetchMediaCategories();

      dispatch(fetchAdvertisementMedias());
    } catch (error) {
      toast.error(
        error.response?.data?.messages ||
          error.response?.data?.message ||
          "Xóa thư mục quảng cáo thất bại"
      );
    }
  };

  const handleOpenMediaModal = (media = null) => {
    setSelectedMedia(media);

    if (currentCategory?.type === "video") {
      setIsVideoModalOpen(true);
    } else {
      setIsImageModalOpen(true);
    }
  };

  const handleCloseMediaModal = () => {
    setSelectedMedia(null);

    setIsImageModalOpen(false);

    setIsVideoModalOpen(false);

    dispatch(fetchAdvertisementMedias());
  };

  if (advertisementStatus === "loading") return <p>Đang tải...</p>;

  if (advertisementStatus === "failed") return <p>Lỗi: {error?.message}</p>;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Quản lý quảng cáo</h1>

      {!adParentCategory ? (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-yellow-700">
          Chưa có danh mục cha <strong>Quảng cáo</strong>. Vui lòng tạo/seed
          danh mục cha có slug <strong>quang-cao</strong>.
        </div>
      ) : (
        <>
          <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  Thư mục quảng cáo
                </h2>

                <p className="text-sm text-gray-500">
                  Mỗi thư mục sẽ có loại ảnh/video riêng. Vị trí hiển thị được
                  hệ thống tự áp dụng.
                </p>
              </div>

              {hasPermission("MEDIA_CREATE") && (
                <button
                  type="button"
                  onClick={() => handleOpenCategoryModal()}
                  className="btn btn-primary btn-sm rounded-md"
                >
                  + Tạo thư mục quảng cáo
                </button>
              )}
            </div>

            {adChildCategories.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {adChildCategories.map((category) => (
                  <div
                    key={category.id}
                    onClick={() => setCurrentCategory(category)}
                    className={`group rounded-lg border transition cursor-pointer p-4 ${
                      Number(currentCategory?.id) === Number(category.id)
                        ? "bg-blue-50 border-blue-300"
                        : "bg-gray-50 border-gray-200 hover:bg-blue-50 hover:border-blue-200"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <FolderIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />

                          <span className="font-medium text-gray-800 truncate">
                            {category.name}
                          </span>
                        </div>

                        <div className="mt-2 flex flex-wrap gap-2">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                              category.type === "video"
                                ? "bg-red-50 text-red-600"
                                : "bg-green-50 text-green-600"
                            }`}
                          >
                            {category.type === "video" ? (
                              <VideoCameraIcon className="w-4 h-4" />
                            ) : (
                              <PhotoIcon className="w-4 h-4" />
                            )}

                            {category.type === "video" ? "Video" : "Ảnh"}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {hasPermission("MEDIA_UPDATE") && (
                          <button
                            type="button"
                            title="Sửa thư mục"
                            className="w-7 h-7 flex items-center justify-center rounded-md border border-blue-200 bg-white text-blue-600 hover:bg-blue-100"
                            onClick={(e) => {
                              e.stopPropagation();

                              handleOpenCategoryModal(category);
                            }}
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        )}

                        {hasPermission("MEDIA_DELETE") && (
                          <button
                            type="button"
                            title="Xóa thư mục"
                            className="w-7 h-7 flex items-center justify-center rounded-md border border-red-200 bg-white text-red-600 hover:bg-red-100"
                            onClick={(e) => {
                              e.stopPropagation();

                              setDeleteCategory(category);
                            }}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Chưa có thư mục quảng cáo
              </p>
            )}
          </div>

          {currentCategory && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  {currentCategory.name}
                </h2>

                <p className="text-sm text-gray-500">
                  Loại:{" "}
                  {currentCategory.type === "video"
                    ? "Video iframe"
                    : "Slider ảnh"}
                </p>
              </div>

              {hasPermission("MEDIA_UPLOAD") && (
                <button
                  type="button"
                  onClick={() => handleOpenMediaModal()}
                  className="btn btn-success btn-sm rounded-md"
                >
                  {currentCategory.type === "video"
                    ? "+ Thêm video"
                    : "+ Thêm ảnh"}
                </button>
              )}
            </div>
          )}

          {currentCategory ? (
            currentMedias.length > 0 ? (
              currentCategory.type === "video" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {currentMedias.map((media) => (
                    <div
                      key={media.id}
                      className="border rounded-lg overflow-hidden shadow-sm cursor-pointer hover:shadow-md transition bg-white"
                      onClick={() => handleOpenMediaModal(media)}
                    >
                      <div className="aspect-video bg-black">
                        <iframe
                          width="100%"
                          height="100%"
                          src={media.url}
                          title={media.title || "Video quảng cáo"}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>

                      <div className="p-3">
                        <p className="font-medium text-gray-800 line-clamp-1">
                          {media.title || "Video quảng cáo"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {currentMedias.map((media) => (
                    <div
                      key={media.id}
                      className="border rounded-lg overflow-hidden shadow-sm cursor-pointer hover:shadow-md transition bg-white"
                      onClick={() => handleOpenMediaModal(media)}
                    >
                      <img
                        src={media.url}
                        alt="Quảng cáo"
                        className="w-full h-32 lg:h-48 object-contain"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              )
            ) : (
              <p className="text-center text-gray-500 mt-20">
                Chưa có nội dung quảng cáo
              </p>
            )
          ) : (
            <p className="text-center text-gray-500 mt-20">
              Vui lòng chọn thư mục quảng cáo
            </p>
          )}
        </>
      )}

      {isCategoryModalOpen && (
        <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-50 flex justify-center items-center">
          <div className="bg-base-100 p-6 rounded-md w-96">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold mb-4">
                {editingCategory
                  ? "Chỉnh sửa thư mục quảng cáo"
                  : "Tạo thư mục quảng cáo"}
              </h2>

              <button
                type="button"
                onClick={handleCloseCategoryModal}
                className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
                disabled={categorySaving}
              >
                X
              </button>
            </div>

            <form onSubmit={handleSaveCategory}>
              <label className="block text-sm mb-2 font-medium">
                Tên thư mục quảng cáo<i className="text-red-600"> *</i>
              </label>

              <input
                type="text"
                placeholder="Nhập tên thư mục quảng cáo"
                value={categoryName}
                onChange={(e) => {
                  setCategoryError("");

                  setCategoryName(e.target.value);
                }}
                className="input input-bordered w-full mb-4"
                disabled={categorySaving}
              />

              <label className="block text-sm mb-2 font-medium">
                Loại quảng cáo<i className="text-red-600"> *</i>
              </label>

              <select
                value={categoryType}
                onChange={(e) => setCategoryType(e.target.value)}
                className="select select-bordered w-full mb-4"
                disabled={categorySaving || !!editingCategory}
              >
                <option value="image">Ảnh / slider ảnh</option>

                <option value="video">Video iframe</option>
              </select>

              {categoryError && (
                <p className="text-red-500 text-sm mb-4">{categoryError}</p>
              )}

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={handleCloseCategoryModal}
                  disabled={categorySaving}
                >
                  Hủy
                </button>

                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={categorySaving}
                >
                  {categorySaving
                    ? "Đang lưu..."
                    : editingCategory
                      ? "Cập nhật"
                      : "Tạo"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteCategory && (
        <div className="fixed inset-0 z-30 bg-gray-500 bg-opacity-50 flex justify-center items-center">
          <div className="bg-base-100 p-6 rounded-md w-96">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold mb-4">
                Xóa thư mục quảng cáo
              </h2>

              <button
                type="button"
                onClick={() => setDeleteCategory(null)}
                className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
              >
                X
              </button>
            </div>

            <p className="mb-6">
              Bạn có chắc muốn xóa thư mục quảng cáo{" "}
              <strong>{deleteCategory.name}</strong> không?
            </p>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setDeleteCategory(null)}
              >
                Hủy
              </button>

              <button
                type="button"
                className="btn btn-error btn-sm"
                onClick={handleConfirmDeleteCategory}
              >
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}

      {isImageModalOpen && currentCategory && (
        <MediaModal
          imageableId={userId}
          imageableType="product"
          imageType="thumbnail"
          mediaCategoryId={currentCategory.id}
          media={selectedMedia}
          onClose={handleCloseMediaModal}
          multiImage={true}
          createTitle="Thêm ảnh quảng cáo"
          detailTitle="Chi tiết ảnh quảng cáo"
          successMessage="Thêm ảnh quảng cáo thành công"
          deleteSuccessMessage="Xóa ảnh quảng cáo thành công"
          showBannerAction={false}
        />
      )}

      {isVideoModalOpen && currentCategory && (
        <AdvertisementVideoModal
          mediaCategoryId={currentCategory.id}
          media={selectedMedia}
          onClose={handleCloseMediaModal}
        />
      )}
    </div>
  );
};

export default AdvertisementMedias;